import { KEYS_TO_NEXT, KEYS_TO_PREVIOUS } from '../../pages/setup/constants'
import { db } from '../../services/db'
import { KEY_SETUP_USER } from '../../utils/constants'
import type { StepsType } from '../../utils/types'
import { useStepContext } from './useStepContext'

export const useSteps = () => {
  const { setCurrentActive } = useStepContext()

  const allSteps = async () => {
    return await db.steps.toArray()
  }

  const bulkRegisterSteps = async (data: StepsType[]) => {
    const steps = await allSteps()
    if (steps.length) return

    db.steps.bulkPut(data)
  }

  const getCurrentStepActive = async () => {
    const step = db.steps.filter((step) => step.active).first()

    if (!step) return null

    return step
  }

  const navigateRouterAutomatic = async () => {
    const currentStepActive = await getCurrentStepActive()
    if (!currentStepActive) return

    setCurrentActive(currentStepActive.id)
  }

  const areSubStepsCompleted = async (parentId: number) => {
    const subSteps = await db.steps
      .filter((step) => step.parent === parentId)
      .toArray()
    return subSteps.every((subStep) => subStep.completed)
  }

  const completeStep = async (stepId: number) => {
    const step = await db.steps.get(stepId)

    if (!step) return

    await db.steps.update(stepId, { completed: true, active: false })

    if (step.parent) {
      const allSubStepsCompleted = await areSubStepsCompleted(step.parent)

      if (allSubStepsCompleted) {
        await db.steps.update(step.parent, { completed: true, active: false })
      }
    }
  }

  const previousStep = async () => {
    const currentStep = await getCurrentStepActive()

    if (!currentStep || currentStep.id === KEY_SETUP_USER) return

    db.transaction('rw', db.steps, async () => {
      await db.steps.update(currentStep.id, { active: false })
      await db.steps.update(KEYS_TO_PREVIOUS[currentStep.id], { active: true })
    }).then(async () => {
      await navigateRouterAutomatic()
    })
  }

  const nextStep = async () => {
    const currentStep = await getCurrentStepActive()

    if (!currentStep) return

    db.transaction('rw', db.steps, async () => {
      await completeStep(currentStep.id)
      await db.steps.update(KEYS_TO_NEXT[currentStep.id], { active: true })
    }).then(async () => {
      await navigateRouterAutomatic()
    })
  }

  return {
    steps: allSteps,
    bulkRegisterSteps,
    getCurrentStepActive,
    previousStep,
    nextStep,
  }
}
